import { getDrillDownLink, getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { DateRangeSpecialValue } from "@components/inputs/date/popup/Calendar.utils";
import { SwitchType } from "@components/inputs/switch/Switch";
import { ifAll, ifAny, IGetValueArgs, not, TTemporalDialogSettings } from "@components/smart/FieldInfo";
import {
    AttachmentsAdditionalProperties,
    AttachmentsDef,
    getAttachmentsColumnDef,
    getAttachmentsFilterDef,
    hasOptionalPostalCode,
    SymbolConstantDef,
    SymbolSpecificDef,
    SymbolVariableDef,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import { ActionButtonPosition } from "@components/smart/smartFastEntryList";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { IColumn, TCellValue } from "@components/table";
import TableWithAutoSizedColumns from "@components/table/TableWithAutoSizedColumns";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    DocumentBusinessPartnerEntity,
    EntitySetName,
    EntityTypeName,
    IPrDeductionEntity,
    IPrDeductionStatusEntity,
    IPrEmployeeEntity,
    OtherLiabilityEntity,
    PrDeductionBusinessPartnerEntity,
    PrDeductionEmployeeEntity,
    PrDeductionEntity,
    PrDeductionPayslipDocumentEntity,
    PrDeductionStatusEntity,
    PrDeductionTemporalEntity,
    PrDeductionUnpaidChildSupportAmountEntity,
    PrEmployeeEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import {
    ClearedStatusCode,
    PrChildSupportDeductionTypeCode,
    PrDeductionCalculationTypeCode,
    PrDeductionStatusCode,
    PrDeductionTypeCode,
    PrDistrainmentDeductionTypeCode,
    PrInsolvencyDepositClosingCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName, getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import {
    getTemporalAdditionalProperties,
    getTemporalPropertyFieldDefinition,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import { getAccountFormatter } from "@pages/accountAssignment/AccountAssignment.utils.shared";
import { userNameWithAvatarFormatter } from "@pages/admin/users/Users.utils";
import {
    getBankAccountFieldsDef,
    getBankAccountsFieldBaseDef,
    paymentDetailFields,
    SAVED_ACCOUNTS_PATH
} from "@pages/banks/bankAccounts/BankAccounts.utils";
import { getBusinessPartnerFieldDef } from "@pages/businessPartner/BusinessPartner.utils";
import { ATTACHMENTS_COLUMN_LOCAL_CONTEXT } from "@pages/documents/Document.utils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "@pages/numberRange/NumberRange.utils";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "@pages/PageUtils";
import {
    ADD_EMPLOYEE_ITEM,
    DEBT_BALANCE_PATH,
    isAmongTypes,
    isDebtTypeOfChildSupport,
    isDecreeRequiredForChildSupport,
    isInsolvencyDepositClosed,
    isPercentageAmountDeduction,
    isRegularChildSupport,
    PAID_AMOUNT_PATH
} from "@pages/payroll/deduction/Deduction.utils";
import DeductionFormView from "@pages/payroll/deduction/DeductionFormView";
import { getEmployeeSelectDef } from "@pages/payroll/employee/Employee.utils";
import {
    createDateFromPeriod,
    getPayrollCurrencyFieldDef,
    getPayrollCurrencyFormatter,
    getPayrollCurrencyUnit
} from "@pages/payroll/payroll.utils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isNewEntity } from "@utils/definition.utils";
import { forEachKey } from "@utils/general";
import { logger } from "@utils/log";
import Big from "big.js";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";
import { ValidationError } from "yup";

import { DASH_CHARACTER } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import {
    BasicInputSizes,
    FieldType,
    GroupedField,
    LabelStatus,
    QueryParam,
    Sort,
    TextAlign,
    ValidatorType
} from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import { ROUTE_DEDUCTIONS, ROUTE_EMPLOYEES, ROUTE_OTHER_LIABILITY, ROUTE_PAYSLIP } from "../../../routes";
import { getQueryParameters } from "../../../routes/Routes.utils";
import CurrencyType from "../../../types/Currency";
import DateType, { DATE_MAX, DateFormat, DisplayFormat, getUtcDayjs } from "../../../types/Date";
import { isNumber } from "../../../types/Number";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { IDeductionCustomData } from "./DeductionFormView";

const DEPOSITED_DEDUCTIONS_FIELD = BindingContext.localContext("DepositedDeductions");

export const getStatusColor = (StatusCode: PrDeductionStatusCode): keyof DefaultTheme => {
    let color: keyof DefaultTheme;

    switch (StatusCode) {
        case PrDeductionStatusCode.Deposited:
        case PrDeductionStatusCode.Ongoing:
            color = "C_SEM_text_good";
            break;
        case PrDeductionStatusCode.Suspended:
            color = "C_SEM_text_warning";
            break;
        default:
            color = "C_TEXT_primary";
            break;
    }

    return color;
};

const statusFormatter: TFormatterFn = (val: TValue, args?: IFormatOptions): TCellValue => {
    const title = val as string;
    const color: keyof DefaultTheme = getStatusColor(args.entity.Status?.Code);

    const colorWrapper = <ColoredText color={color}> {val} </ColoredText>;

    return { tooltip: title, value: colorWrapper };
};

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrDeduction}Table`,
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrDeductionEntity.NumberOurs,
                PrDeductionEntity.Name,
                PrDeductionEntity.Type,
                PrDeductionEntity.Status,
                PrDeductionEntity.DateStart,
                PrDeductionEntity.DateEnd,
            ],
            filterDefinition: {
                [PrDeductionEntity.NumberOurs]: {},
                [PrDeductionEntity.Name]: {},
                ...withDisplayName(PrDeductionEntity.Type),
                ...withDisplayName(PrDeductionEntity.Status),
                [PrDeductionEntity.DateStart]: {},
                [PrDeductionEntity.DateEnd]: {},
                [ATTACHMENTS_COLUMN_LOCAL_CONTEXT]: getAttachmentsFilterDef(),
            },
            isValueHelp: true
        }],
        initialSortBy: [{
            id: PrDeductionEntity.NumberOurs,
            sort: Sort.Desc
        }],
        columns: [
            PrDeductionEntity.NumberOurs,
            PrDeductionEntity.Name,
            PrDeductionEntity.Type,
            PrDeductionEntity.Status,
            PrDeductionEntity.DateStart,
            PrDeductionEntity.DateEnd,
        ],
        columnDefinition: {
            [PrDeductionEntity.NumberOurs]: {},
            [PrDeductionEntity.Name]: {},
            ...withDisplayName(PrDeductionEntity.Type),
            ...withDisplayName(PrDeductionEntity.Status, "Name", { formatter: statusFormatter }),
            [PrDeductionEntity.DateStart]: {},
            [PrDeductionEntity.DateEnd]: {},
            [PrEmployeeEntity.Attachments]: getAttachmentsColumnDef(EntityTypeName.PrEmployee)
        },
        title: i18next.t("Deduction:TableTitle")
    };

    const summary: ISummaryItem[] = [{
        id: PrDeductionEntity.Name,
        label: null
    }, {
        ...getNumberOursSummaryDef("Deduction")
    }, {
        id: PrDeductionEntity.TypeCode,
        label: i18next.t("Deduction:Form.Type"),
        formatter: (val, args) => {
            return getEnumDisplayValue(EntityTypeName.PrDeductionType, args.entity.TypeCode as PrDeductionTypeCode);
        },
    }, {
        id: PrDeductionEntity.Status,
        label: i18next.t("Deduction:Form.Status"),
        additionalProperties: [{ id: PrDeductionStatusEntity.Name }],
        formatter: (val: TValue) => {
            const status = val as IPrDeductionStatusEntity;
            return status?.Name;
        },
        colorFormatter: (val: TValue) => {
            const status = val as IPrDeductionStatusEntity;
            return getStatusColor(status?.Code as PrDeductionStatusCode);
        },
        isVisible: not(isNewEntity)
    }];

    const bankAccountFields = getBankAccountsFieldBaseDef();
    const bankAccountDef = getBankAccountFieldsDef({ addPaymentMethod: false, areFieldsDisabled: false, type: null });

    const isCounterparty = (args: IGetValueArgs): boolean => {
        const entity = args.storage.data.entity as IPrDeductionEntity;
        return entity.TypeCode !== PrDeductionTypeCode.Custom || !entity.IsCompanyOwn;
    };

    const recipientFields = getBusinessPartnerFieldDef(
        isAmongTypes([PrDeductionTypeCode.Distrainment, PrDeductionTypeCode.Insolvency, PrDeductionTypeCode.ChildSupport, PrDeductionTypeCode.Custom]) && isCounterparty,
        null,
        PrDeductionEntity.Recipient
    );

    const calculationTypeTemporalDialogSettings: TTemporalDialogSettings = {
        dialogTitle: i18next.t("Deduction:Form.PlanDeductionTitle"),
        columns: [
            PrDeductionTemporalEntity.CalculationType,
            PrDeductionTemporalEntity.Amount
        ],
    };

    const taxNumberRecipientField = recipientFields[createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.TaxNumber)];

    const commonNameEmployeeRow = [
        { id: PrDeductionEntity.Name },
        { id: PrDeductionEntity.Employee }
    ];
    const commonDateRow = [
        { id: PrDeductionEntity.DateStart },
        { id: PrDeductionEntity.DateEnd }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.PrDeduction}Form`,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Deduction:NewDeduction"), storage.data.entity?.NumberOurs),
        translationFiles: getDefinitions.translationFiles,
        isDeletable: (args) => !(args.storage.data.entity as IPrDeductionEntity).InsolvencyDepositClosingCode,
        ignoreVariants: true,
        formControl: DeductionFormView,
        files: AttachmentsDef,
        summary,
        additionalProperties: [
            ...getTemporalAdditionalProperties(PrDeductionTemporalEntity, PrDeductionEntity),
            { id: PrDeductionEntity.TypeCode },
            ...NumberRangeAdditionalProperties,
            ...AttachmentsAdditionalProperties,
            {
                id: PrDeductionEntity.DepositClosingDocument,
                additionalProperties: [
                    { id: OtherLiabilityEntity.ClearedStatusCode },
                    { id: OtherLiabilityEntity.NumberOurs },
                    { id: OtherLiabilityEntity.Amount },
                    { id: OtherLiabilityEntity.DateDue },
                    {
                        id: OtherLiabilityEntity.BusinessPartner,
                        additionalProperties: [{ id: DocumentBusinessPartnerEntity.Name }],
                    },
                ],
            },
            {
                id: PrDeductionEntity.UnpaidChildSupportAmounts,
                additionalProperties: [
                    { id: PrDeductionUnpaidChildSupportAmountEntity.Amount },
                    {
                        id: PrDeductionUnpaidChildSupportAmountEntity.Payslip,
                        additionalProperties: [{ id: "Month" }, { id: "Year" }],
                    },
                ],
            },
        ],
        isReadOnly: isInsolvencyDepositClosed,
        fieldDefinition: {
            ...TemporalValidityFieldsDef,
            [PrDeductionEntity.Name]: {
                width: BasicInputSizes.L,
            },
            [PrDeductionEntity.TypeCode]: {
                isVisible: false,
                clearIfInvisible: false,
                defaultValue: (args: IGetValueArgs) => {
                    const key = getQueryParameters()[QueryParam.Type];
                    if (Object.values(PrDeductionTypeCode).includes(key as PrDeductionTypeCode)) {
                        return key;
                    }
                    logger.error("Invalid type code");
                    return null;
                },
                customizationData: {
                    useForCustomization: false,
                },
            },
            ...getTemporalPropertyFieldDefinition({
                propName: PrDeductionTemporalEntity.Amount,
                definition: {
                    ...getPayrollCurrencyFieldDef(),
                    width: BasicInputSizes.M,
                    isRequired: true,
                    isVisible:
                        ifAny(
                            isRegularChildSupport,
                            not(isAmongTypes([
                                PrDeductionTypeCode.Distrainment,
                                PrDeductionTypeCode.Insolvency,
                                PrDeductionTypeCode.InsolvencyDeposit,
                                PrDeductionTypeCode.ChildSupport
                            ]))
                        ),
                    validator: {
                        type: ValidatorType.Number,
                        settings: {
                            min: 0,
                            customValidator: (value: TValue, args: IGetValueArgs): boolean | ValidationError => {
                                if (isPercentageAmountDeduction(args)) {
                                    const amount = value as number;
                                    if (amount > 100) {
                                        return new ValidationError(i18next.t("Common:Validation.BigNumber", { max: 100 }), value, args.bindingContext.getEntityPath(true));
                                    }
                                }
                                return true;
                            }
                        }
                    },
                    fieldSettings: {
                        unit: (args): string => {
                            if (isPercentageAmountDeduction(args)) {
                                return "%";
                            }
                            return getPayrollCurrencyUnit()(args);
                        },
                        temporalDialog: calculationTypeTemporalDialogSettings,
                    },
                },
                withOpeningButton: true,
            }),
            [PrDeductionEntity.InsolvencyDepositClosingCode]: {
                type: FieldType.ComboBox,
                clearIfInvisible: false,
                isRequired: true,
                label: i18next.t("Deduction:Form.InsolvencyDepositClosingCode"),
                fieldSettings: {
                    items: [
                        {
                            id: PrInsolvencyDepositClosingCode.KeepOnAccount,
                            label: i18next.t("Deduction:Form.InsolvencyDepositClosingCodeKeep"),
                        },
                        {
                            id: PrInsolvencyDepositClosingCode.TransferToInsolvencyAdministrator,
                            label: i18next.t("Deduction:Form.InsolvencyDepositClosingCodeTransfer"),
                        },
                    ],
                },
            },
            [PrDeductionEntity.DateLegallyEffective]: {
                isVisible: (args: IGetValueArgs) =>
                    args.storage.getValue(
                        args.storage.data.bindingContext.navigate(PrDeductionEntity.InsolvencyDepositClosingCode)
                    ) === PrInsolvencyDepositClosingCode.TransferToInsolvencyAdministrator,
                clearIfInvisible: false,
            },
            ...getTemporalPropertyFieldDefinition({
                propName: PrDeductionTemporalEntity.CalculationType,
                definition: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.L,
                    isVisible: isAmongTypes([PrDeductionTypeCode.Custom]),
                    isRequired: isAmongTypes([PrDeductionTypeCode.Custom]),
                    defaultValue: PrDeductionCalculationTypeCode.Fixed,
                    fieldSettings: {
                        items: getEnumSelectItems(EntityTypeName.PrDeductionCalculationType),
                        temporalDialog: calculationTypeTemporalDialogSettings,
                    },
                    clearIfInvisible: false,
                    affectedFields: [
                        {
                            id: createPath(PrDeductionEntity.CurrentTemporalPropertyBag, PrDeductionTemporalEntity.Amount),
                            revalidate: true
                        },
                    ]
                },
                withOpeningButton: true,
            }),
            [PrDeductionEntity.DateStart]: {
                type: FieldType.MonthYear,
                groupedField: (args: IGetValueArgs) => {
                    if (isAmongTypes([PrDeductionTypeCode.InsolvencyDeposit])(args)) {
                        return null;
                    }
                    return GroupedField.MultiStart;
                },
                defaultValue: getUtcDayjs().startOf("month").startOf("day").toDate(),
                affectedFields: [{ id: PrDeductionEntity.DateEnd, revalidate: true }]
            },
            [PrDeductionEntity.DateEnd]: {
                type: FieldType.MonthYear,
                groupedField: GroupedField.MultiEnd,
                defaultValue: DATE_MAX,
                isVisible: not(isAmongTypes([PrDeductionTypeCode.InsolvencyDeposit])),
                clearIfInvisible: false,
                fieldSettings: {
                    showSpecialValue: DateRangeSpecialValue.WithoutEnd,
                },
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: (value: TValue, args: IGetValueArgs): boolean | ValidationError => {
                            const startDate = args.storage.getValue(args.storage.data.bindingContext.navigate(PrDeductionEntity.DateStart)) as Date;
                            if (startDate && value) {
                                const start = getUtcDayjs(startDate).startOf("month").startOf("day");
                                const end = getUtcDayjs(value as Date).startOf("month").startOf("day");
                                if (end.isBefore(start)) {
                                    return new ValidationError(i18next.t("Deduction:Validation.DateEndIsBeforeDateStart"), value, args.bindingContext.getPath(true));
                                }
                            }
                            return true;
                        }
                    }
                }
            },
            ...getNumberRangeFieldDefs("Deduction"),
            ...recipientFields,
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Name)]: {
                ...recipientFields[createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Name)],
                isVisible: isCounterparty
            },
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.LegalNumber)]: {
                ...recipientFields[
                    createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.LegalNumber)
                    ],
                groupedField: GroupedField.WrappedMultiEnd,
                isVisible: isCounterparty
            },
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.TaxNumber)]: {
                ...taxNumberRecipientField,
                collapsedRows: [
                    [
                        { id: `${createPath(PrDeductionEntity.Recipient)}/Street` },
                        { id: `${createPath(PrDeductionEntity.Recipient)}/City` },
                        { id: `${createPath(PrDeductionEntity.Recipient)}/PostalCode` },
                        { id: `${createPath(PrDeductionEntity.Recipient)}/Country` },
                    ],
                ],
                isVisible: false,
                isRequired: false,
            },
            // collapsedRows are still visible if isVisible == false, so this is workaround
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Street)]: {
                ...recipientFields[createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Street)],
                isVisible: isCounterparty,
                clearIfInvisible: true,
                isRequired: true
            },
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.City)]: {
                ...recipientFields[createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.City)],
                isVisible: isCounterparty,
                clearIfInvisible: true,
                isRequired: true
            },
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.PostalCode)]: {
                ...recipientFields[
                    createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.PostalCode)
                    ],
                isVisible: isCounterparty,
                isRequired: not(hasOptionalPostalCode),
                clearIfInvisible: true,
            },
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Country)]: {
                ...recipientFields[createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Country)],
                isVisible: isCounterparty,
                clearIfInvisible: true,
                isRequired: true
            },
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.VatStatusCode)]: {
                isVisible: false,
                isRequired: false,
                clearIfInvisible: false,
            },
            [createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.CountryCode)]: {
                isVisible: false,
                isRequired: false,
                clearIfInvisible: false,
            },
            [PrDeductionEntity.IsCompanyOwn]: {
                type: FieldType.SegmentedButton,
                labelStatus: LabelStatus.Removed,
                isVisible: isAmongTypes([PrDeductionTypeCode.Custom]),
                defaultValue: false,

                fieldSettings: {
                    items: [
                        { id: false, label: i18next.t("Deduction:Form.RecipientCounterparty") },
                        { id: true, label: i18next.t("Deduction:Form.RecipientEmployer") },
                    ],
                },
            },
            [PrDeductionEntity.PayableAccountNumber]: {
                type: FieldType.ComboBox,
                isRequired: ifAll(
                    isAmongTypes([PrDeductionTypeCode.Custom]),
                    (args: IGetValueArgs) => !!(args.storage.data.entity as IPrDeductionEntity).IsCompanyOwn
                ),
                isVisible: ifAll(
                    isAmongTypes([PrDeductionTypeCode.Custom]),
                    (args: IGetValueArgs) => !!(args.storage.data.entity as IPrDeductionEntity).IsCompanyOwn
                ),
                filter: {
                    select: "startswith(Number, '3')",
                },
                columns: [
                    {
                        id: "Number",
                        additionalProperties: [
                            {
                                id: "/Name",
                            },
                        ],
                        formatter: getAccountFormatter(""), // format from flatten accounts where is the prop without prefix
                    },
                ],
                formatter: (val: TValue, args: IFormatOptions) => {
                    // format as value only (AccountNumber, e.g. "015") -> we don't want to show the account name
                    // as the field is too small
                    return val as string;
                },
                fieldSettings: {
                    entitySet: EntitySetName.FlattenAccounts,
                    idName: "Number",
                },
            },
            [PrDeductionEntity.SymbolVariable]: { ...SymbolVariableDef, isVisible: isCounterparty },
            [PrDeductionEntity.SymbolSpecific]: { ...SymbolSpecificDef, isVisible: isCounterparty },
            [PrDeductionEntity.SymbolConstant]: { ...SymbolConstantDef, isVisible: isCounterparty },
            [PrDeductionEntity.RemittanceInformation]: { isVisible: isCounterparty },
            [SAVED_ACCOUNTS_PATH]: {
                ...bankAccountDef[SAVED_ACCOUNTS_PATH],
                isVisible: isCounterparty,
                isRequired: isCounterparty,
                collapsedRows: [
                    [{ id: "BankAccount/Country" }],
                    [
                        { id: "BankAccount/AccountNumber" },
                        { id: "BankAccount/Bank" },
                        { id: "BankAccount/IBAN" },
                        { id: "BankAccount/SWIFT" },
                    ],
                ],
            },
            [PrDeductionEntity.Employee]: {
                ...getEmployeeSelectDef(),
                isRequired: not(isAmongTypes([PrDeductionTypeCode.Custom])),
                isVisible: not(isAmongTypes([PrDeductionTypeCode.Custom])),
            },
            [PrDeductionEntity.UnpaidChildSupportAmounts]: {
                additionalProperties: [
                    {
                        id: PrDeductionUnpaidChildSupportAmountEntity.Payslip,
                    },
                ],
                isVisible: isDebtTypeOfChildSupport,
                render: (args) => {
                    const unpaidAmounts = (args?.storage?.data.entity as IPrDeductionEntity).UnpaidChildSupportAmounts;
                    const columns: IColumn[] = [
                        { id: "payslip", label: i18next.t("Deduction:Form.Payslip"), textAlign: TextAlign.Left },
                        { id: "month", label: i18next.t("Deduction:Form.ForPeriod"), textAlign: TextAlign.Left },
                        { id: "amount", label: i18next.t("Deduction:Form.Amount"), textAlign: TextAlign.Right },
                    ];

                    const rows =
                        unpaidAmounts.length > 0
                            ? unpaidAmounts.map((unpaidAm) => ({
                                id: unpaidAm.Id,
                                values: {
                                    payslip: unpaidAm?.Payslip?.Id,
                                    month: unpaidAm.Payslip
                                        ? DateType.format(
                                            getUtcDayjs()
                                                .set("month", unpaidAm.Payslip.Month - 1)
                                                .set("year", unpaidAm.Payslip.Year)
                                                .toDate(),
                                            DateFormat.monthAndYear
                                        )
                                        : DASH_CHARACTER,
                                    amount: CurrencyType.format(unpaidAm.Amount, { currency: getCompanyCurrency(args.storage.context) })
                                },
                            }))
                            : [];
                    return (
                        <TableWithAutoSizedColumns
                            rows={rows}
                            columns={columns}
                            disableSort={true}
                            disableVirtualization={true}
                        />
                    );
                },
            },
            [PrDeductionEntity.PayslipDocuments]: {
                additionalProperties: [
                    {
                        id: PrDeductionPayslipDocumentEntity.Document,
                    },
                    { id: createPath(PrDeductionPayslipDocumentEntity.Document, OtherLiabilityEntity.NumberOurs) },
                    {
                        id: PrDeductionPayslipDocumentEntity.Payslip,
                        additionalProperties: [
                            {
                                id: PrPayslipEntity.Month,
                            },
                            {
                                id: PrPayslipEntity.Year,
                            }
                        ]
                    },
                ],
                type: FieldType.Custom,
                isVisible: (args) => {
                    const entity = args?.storage?.data.entity as IPrDeductionEntity;
                    return entity.PayslipDocuments?.length > 0;
                },
                render: (args) => {
                    const payslipDocs = (args?.storage?.data.entity as IPrDeductionEntity).PayslipDocuments;

                    const columns: IColumn[] = [
                        { id: "month", label: i18next.t("Deduction:Form.Payslip"), textAlign: TextAlign.Left },
                        {
                            id: "payslipDoc",
                            label: i18next.t("Deduction:Form.SettledByDocument"),
                            textAlign: TextAlign.Right,
                        },
                    ];

                    const rows = payslipDocs.map((psDoc) => ({
                        id: psDoc.Id,
                        values: {
                            month: psDoc.Payslip && psDoc.Payslip.Month && psDoc.Payslip.Year && psDoc.Payslip.Id
                                ? getTableIntentLink(DateType.format(createDateFromPeriod(psDoc.Payslip), DateFormat.monthAndYear), {
                                    route: `${ROUTE_PAYSLIP}/${psDoc.Payslip.Id}`,
                                    context: args.storage.context,
                                })
                                : DASH_CHARACTER,
                            payslipDoc: psDoc.Document?.NumberOurs
                                ? getTableIntentLink(psDoc.Document?.NumberOurs?.toString(), {
                                    route: `${ROUTE_OTHER_LIABILITY}/${psDoc.Document?.Id?.toString()}`,
                                    context: args.storage.context,
                                })
                                : DASH_CHARACTER,
                        },
                    }));
                    return (
                        <TableWithAutoSizedColumns
                            rows={rows}
                            columns={columns}
                            disableSort={true}
                            disableVirtualization={true}
                        />
                    );
                },
            },
            [PrDeductionEntity.DateReceived]: {
                isRequired: isAmongTypes([
                    PrDeductionTypeCode.Custom,
                    PrDeductionTypeCode.Distrainment,
                    PrDeductionTypeCode.ChildSupport,
                    PrDeductionTypeCode.InsolvencyDeposit,
                    PrDeductionTypeCode.InsolvencyAdministratorReward,
                ]),
                isVisible: (args) => {
                    return isAmongTypes([
                        PrDeductionTypeCode.ChildSupport,
                        PrDeductionTypeCode.Custom,
                        PrDeductionTypeCode.Distrainment,
                        PrDeductionTypeCode.InsolvencyDeposit,
                        PrDeductionTypeCode.InsolvencyAdministratorReward,
                    ])(args);
                },
            },
            [PrDeductionEntity.DepositedAmount]: {
                isVisible: isAmongTypes([PrDeductionTypeCode.InsolvencyDeposit]),
                isReadOnly: true,
                isRequired: false,
                formatter: (val, args) => getPayrollCurrencyFormatter(undefined, true)(val, args),
            },
            [PrDeductionEntity.DecreeNumber]: {
                isRequired: ifAny(
                    isDecreeRequiredForChildSupport,
                    isAmongTypes([
                        PrDeductionTypeCode.Insolvency,
                        PrDeductionTypeCode.Distrainment,
                        PrDeductionTypeCode.InsolvencyDeposit,
                        PrDeductionTypeCode.InsolvencyAdministratorReward,
                    ])
                ),
                isVisible: isAmongTypes([
                    PrDeductionTypeCode.Insolvency,
                    PrDeductionTypeCode.Distrainment,
                    PrDeductionTypeCode.ChildSupport,
                    PrDeductionTypeCode.InsolvencyDeposit,
                    PrDeductionTypeCode.InsolvencyAdministratorReward,
                ]),
            },
            [PrDeductionEntity.AmountDebtTotal]: {
                ...getPayrollCurrencyFieldDef(),
                label: i18next.t("Deduction:Form.AmountDebtTotal"),
                isRequired: isAmongTypes([PrDeductionTypeCode.Distrainment]),
                isVisible: ifAll(
                    isDebtTypeOfChildSupport,
                    isAmongTypes([PrDeductionTypeCode.Distrainment, PrDeductionTypeCode.ChildSupport])
                ),
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0
                    }
                },
                affectedFields: [
                    { id: PAID_AMOUNT_PATH },
                    { id: DEBT_BALANCE_PATH },
                ],
            },
            [PrDeductionEntity.DistrainmentType]: {
                type: FieldType.SegmentedButton,
                labelStatus: LabelStatus.Removed,
                defaultValue: PrDistrainmentDeductionTypeCode.Priority,
                isVisible: isAmongTypes([PrDeductionTypeCode.Distrainment]),
                fieldSettings: {
                    items: [
                        {
                            id: PrDistrainmentDeductionTypeCode.Priority,
                            label: getEnumDisplayValue(
                                EntityTypeName.PrDistrainmentDeductionType,
                                PrDistrainmentDeductionTypeCode.Priority
                            ),
                        },
                        {
                            id: PrDistrainmentDeductionTypeCode.Standard,
                            label: getEnumDisplayValue(
                                EntityTypeName.PrDistrainmentDeductionType,
                                PrDistrainmentDeductionTypeCode.Standard
                            ),
                        },
                    ],
                },
            },
            [PrDeductionEntity.ChildSupportType]: {
                type: FieldType.SegmentedButton,
                defaultValue: PrChildSupportDeductionTypeCode.Regular,
                isVisible: isAmongTypes([PrDeductionTypeCode.ChildSupport]),
                affectedFields: [{ id: PrDeductionEntity.DecreeNumber }],
                fieldSettings: {
                    items: [
                        {
                            id: PrChildSupportDeductionTypeCode.Regular,
                            label: getEnumDisplayValue(
                                EntityTypeName.PrChildSupportDeductionType,
                                PrChildSupportDeductionTypeCode.Regular
                            ),
                        },
                        {
                            id: PrChildSupportDeductionTypeCode.Owed,
                            label: getEnumDisplayValue(
                                EntityTypeName.PrChildSupportDeductionType,
                                PrChildSupportDeductionTypeCode.Owed
                            ),
                        },
                        {
                            id: PrChildSupportDeductionTypeCode.Assigned,
                            label: getEnumDisplayValue(
                                EntityTypeName.PrChildSupportDeductionType,
                                PrChildSupportDeductionTypeCode.Assigned
                            ),
                        },
                        {
                            id: PrChildSupportDeductionTypeCode.Substitute,
                            label: getEnumDisplayValue(
                                EntityTypeName.PrChildSupportDeductionType,
                                PrChildSupportDeductionTypeCode.Substitute
                            ),
                        },
                    ],
                },
            },
            [PrDeductionEntity.IsRegularSupportIncludedInInsolvency]: {
                type: FieldType.Switch,
                isVisible: isAmongTypes([PrDeductionTypeCode.Insolvency]),
                fieldSettings: {
                    type: SwitchType.YesNo,
                },
            },
            [PAID_AMOUNT_PATH]: {
                label: i18next.t("Deduction:Form.PaidAmount"),
                isReadOnly: true,
                isVisible: isAmongTypes([PrDeductionTypeCode.Distrainment]),
                fieldSettings: {
                    unit: getPayrollCurrencyUnit(),
                },
                formatter: (_, args: IFormatOptions) => {
                    const entity = args.entity as IPrDeductionEntity;
                    const totalDebt = entity.AmountDebtTotal;

                    if (!isNumber(totalDebt)) {
                        return DASH_CHARACTER;
                    }

                    const paidAmount = entity.PayslipDocuments?.reduce((acc, payment) => {
                        return acc.add(payment.Document?.Amount ?? 0);
                    }, new Big(0)).toNumber() ?? 0;

                    return getPayrollCurrencyFormatter()(paidAmount, args);
                },
            },
            [DEBT_BALANCE_PATH]: {
                label: i18next.t("Deduction:Form.DebtBalance"),
                isVisible: ifAll(
                    isDebtTypeOfChildSupport,
                    isAmongTypes([PrDeductionTypeCode.Distrainment, PrDeductionTypeCode.ChildSupport])
                ),
                additionalProperties: [
                    {
                        id: PrDeductionEntity.AmountDebtTotal,
                    },
                    {
                        id: PrDeductionEntity.PayslipDocuments,
                        additionalProperties: [
                            {
                                id: PrDeductionPayslipDocumentEntity.Document,
                                additionalProperties: [
                                    {
                                        id: OtherLiabilityEntity.Amount,
                                    },
                                ],
                            },
                        ],
                    },
                ],
                isReadOnly: true,
                formatter: (val: TValue, args: IFormatOptions) => {
                    const entity = args.entity as IPrDeductionEntity;
                    const totalDebt = entity.AmountDebtTotal;

                    if (!isNumber(totalDebt)) {
                        return DASH_CHARACTER;
                    }

                    const paidAmount =
                        entity.PayslipDocuments?.reduce((acc, payment) => {
                            return acc.add(payment.Document?.Amount ?? 0);
                        }, new Big(0)) ?? new Big(0);

                    const balance = new Big(totalDebt).sub(paidAmount).toNumber();
                    return getPayrollCurrencyFormatter()(balance, args);
                },
                fieldSettings: {
                    unit: getPayrollCurrencyUnit(),
                },
            },
            [createPath(PrDeductionEntity.Employees, PrDeductionEmployeeEntity.Employee, PrEmployeeEntity.NumberOurs)]:
                {
                    isReadOnly: true,
                },
            ...getTemporalPropertyFieldDefinition({
                propName: PrDeductionTemporalEntity.IsIncludingAllEmployees,
                definition: {
                    type: FieldType.Switch,
                    defaultValue: false,
                    isVisible: not(
                        isAmongTypes([
                            PrDeductionTypeCode.InsolvencyDeposit,
                            PrDeductionTypeCode.InsolvencyAdministratorReward,
                            PrDeductionTypeCode.ChildSupport,
                        ])
                    ),
                    fieldSettings: {
                        type: SwitchType.YesNo,
                    },
                },
                withOpeningButton: false,
            }),
            [createPath(PrDeductionEntity.Employees, PrDeductionEmployeeEntity.Employee, PrEmployeeEntity.LastName)]: {
                isReadOnly: true,
            },
            [DEPOSITED_DEDUCTIONS_FIELD]: {
                type: FieldType.Custom,
                isVisible: (args) => {
                    const deductions = (args.storage.getCustomData() as IDeductionCustomData)?.depositedDeductions;
                    return (
                        !!deductions &&
                        deductions.length > 0 &&
                        isAmongTypes([PrDeductionTypeCode.InsolvencyDeposit])(args)
                    );
                },
                render: (args) => {
                    const deductions = (args.storage.getCustomData() as IDeductionCustomData)?.depositedDeductions;
                    if (!deductions?.length) {
                        return null;
                    }
                    const columns: IColumn[] = [
                        { id: "month", label: i18next.t("Deduction:Form.PaymentMonth"), textAlign: TextAlign.Left },
                        { id: "number", label: i18next.t("Deduction:Form.DeductionNumber"), textAlign: TextAlign.Left },
                        { id: "name", label: i18next.t("Deduction:Form.DeductionName"), textAlign: TextAlign.Left },
                        {
                            id: "amount",
                            label: i18next.t("Deduction:Form.DeductionAmount"),
                            textAlign: TextAlign.Right,
                        },
                    ];

                    const rows = deductions.map((d) => ({
                        id: d.Id,
                        drilldown: d.DepositedDeduction?.Id
                            ? getDrillDownLink("", {
                                route: `${ROUTE_DEDUCTIONS}/${d.DepositedDeduction.Id}`,
                                context: args.storage.context,
                            })
                            : undefined,
                        values: {
                            month: d.Payslip
                                ? getTableIntentLink(
                                    DateType.format(
                                        getUtcDayjs()
                                            .set("month", d.Payslip.Month - 1)
                                            .set("year", d.Payslip.Year)
                                            .toDate(),
                                        DateFormat.monthAndYear
                                    ),
                                    { route: `${ROUTE_PAYSLIP}/${d.Payslip.Id}`, context: args.storage.context }
                                )
                                : DASH_CHARACTER,
                            number: d.DepositedDeduction?.NumberOurs
                                ? getTableIntentLink(d.DepositedDeduction.NumberOurs, {
                                    route: `${ROUTE_DEDUCTIONS}/${d.DepositedDeduction.Id}`,
                                    context: args.storage.context,
                                })
                                : DASH_CHARACTER,
                            name: d.DepositedDeduction?.Name ?? DASH_CHARACTER,
                            amount: getPayrollCurrencyFormatter(undefined, true)(d.DepositedAmount, args),
                        },
                    }));
                    return (
                        <TableWithAutoSizedColumns
                            rows={rows}
                            columns={columns}
                            disableSort={true}
                            disableVirtualization={true}
                        />
                    );
                },
            },
            [PrDeductionEntity.DepositClosingDocument]: {
                type: FieldType.Custom,
                render: (args) => {
                    const entity = args.storage.data.entity as IPrDeductionEntity;
                    const doc = entity.DepositClosingDocument;
                    if (!doc?.NumberOurs) {
                        return null;
                    }
                    const columns: IColumn[] = [
                        { id: "number", label: i18next.t("Deduction:Form.DocumentNumber"), textAlign: TextAlign.Left },
                        { id: "amount", label: i18next.t("Deduction:Form.Amount"), textAlign: TextAlign.Right },
                        {
                            id: "businessPartner",
                            label: i18next.t("Deduction:Form.Receiver"),
                            textAlign: TextAlign.Left,
                        },
                        { id: "dateDue", label: i18next.t("Deduction:Form.DateDue"), textAlign: TextAlign.Left },
                        { id: "clearedStatus", label: i18next.t("Deduction:Form.Status"), textAlign: TextAlign.Left },
                    ];
                    const rows = [
                        {
                            id: doc.Id,
                            values: {
                                number: getTableIntentLink(doc.NumberOurs, {
                                    route: `${ROUTE_OTHER_LIABILITY}/${doc.Id}`,
                                    context: args.storage.context,
                                }),
                                amount: getPayrollCurrencyFormatter(undefined, true)(doc.Amount, args),
                                businessPartner: doc.BusinessPartner?.Name ?? DASH_CHARACTER,
                                dateDue: doc.DateDue
                                    ? DateType.format(doc.DateDue, DisplayFormat.DateMedium)
                                    : DASH_CHARACTER,
                                clearedStatus: (() => {
                                    if (!doc.ClearedStatusCode) {
                                        return DASH_CHARACTER;
                                    }
                                    const title = getEnumDisplayValue(
                                        EntityTypeName.ClearedStatus,
                                        doc.ClearedStatusCode
                                    );
                                    let color: string;
                                    switch (doc.ClearedStatusCode) {
                                        case ClearedStatusCode.Cleared:
                                            color = "C_SEM_text_good";
                                            break;
                                        case ClearedStatusCode.PartiallyCleared:
                                            color = "C_SEM_text_warning";
                                            break;
                                        case ClearedStatusCode.NotCleared:
                                            color = "C_SEM_text_bad";
                                    }
                                    return { tooltip: title, value: <ColoredText color={color}>{title}</ColoredText> };
                                })(),
                            },
                        },
                    ];
                    return (
                        <TableWithAutoSizedColumns
                            rows={rows}
                            columns={columns}
                            disableSort={true}
                            disableVirtualization={true}
                        />
                    );
                },
            },
        },
        groups: [
            {
                id: "main",
                rows: [
                    commonNameEmployeeRow,
                    [
                        ...commonDateRow,
                        {
                            id: PrDeductionEntity.IsRegularSupportIncludedInInsolvency,
                            isVisible: isAmongTypes([PrDeductionTypeCode.Insolvency]),
                        },
                    ],
                ],
            },
            {
                id: "detail",
                title: i18next.t("Deduction:Form.DeductionDetail"),
                rows: [
                    [{ id: PrDeductionEntity.DistrainmentType }, { id: PrDeductionEntity.ChildSupportType }],
                    [
                        { id: PrDeductionEntity.DecreeNumber },
                        { id: PrDeductionEntity.DateReceived },
                        { id: createPath(PrDeductionEntity.CurrentTemporalPropertyBag, PrDeductionTemporalEntity.CalculationType) },
                        { id: createPath(PrDeductionEntity.CurrentTemporalPropertyBag, PrDeductionTemporalEntity.Amount) }
                    ],
                    [
                        { id: PrDeductionEntity.AmountDebtTotal },
                        { id: PAID_AMOUNT_PATH },
                        { id: DEBT_BALANCE_PATH },
                        { id: PrDeductionEntity.DepositedAmount },
                    ],
                ],
            },
            {
                id: "receiver",
                title: i18next.t("Deduction:Form.Receiver"),
                rows: [
                    [{ id: PrDeductionEntity.IsCompanyOwn }],
                    [
                        { id: createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Name) },
                        { id: createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.LegalNumber) },
                        { id: createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.TaxNumber) },
                    ],
                    [{ id: PrDeductionEntity.PayableAccountNumber }],
                    [{ id: SAVED_ACCOUNTS_PATH }],
                    [...paymentDetailFields],
                ],
            },
            {
                id: "employees",
                title: i18next.t("Deduction:Form.EmployeesGroupTitle"),
                rows: [
                    [
                        {
                            id: createPath(
                                PrDeductionEntity.CurrentTemporalPropertyBag,
                                PrDeductionTemporalEntity.IsIncludingAllEmployees
                            ),
                        },
                    ],
                ],
                isVisible: isAmongTypes([PrDeductionTypeCode.Custom]),
                lineItems: {
                    collection: PrDeductionEntity.Employees,
                    canAdd: false,
                    canReorder: false,
                    order: createPath(PrDeductionEmployeeEntity.Employee, PrEmployeeEntity.NumberOurs),
                    preserveOrderOnRemove: true,
                    isTheOnlyItemRemovable: true,
                    disableVirtualization: true,
                    additionalFields: [
                        { id: createPath(PrDeductionEmployeeEntity.Employee, PrEmployeeEntity.FirstName) },
                    ],
                    isItemVisible: (args) => {
                        const deduction = args.storage.data.entity as IPrDeductionEntity;
                        return deduction.CurrentTemporalPropertyBag?.IsIncludingAllEmployees === false;
                    },
                    columns: [
                        {
                            id: createPath(PrDeductionEmployeeEntity.Employee, PrEmployeeEntity.NumberOurs),
                            label: i18next.t("Deduction:Form.EmployeeNumber"),
                            formatter: (_: TValue, args: IFormatOptions) => {
                                const employee = args.storage.getValue(args.bindingContext.getParent()) as IPrEmployeeEntity;
                                if (!employee?.NumberOurs) {
                                    return DASH_CHARACTER;
                                }
                                return getTableIntentLink(employee.NumberOurs?.toString(), {
                                    route: `${ROUTE_EMPLOYEES}/${employee.Id}`,
                                    context: args.storage.context,
                                });
                            }
                        },
                        {
                            id: createPath(PrDeductionEmployeeEntity.Employee, PrEmployeeEntity.LastName),
                            label: i18next.t("Employee:Table.EmployeeName"),
                            width: "fit-content",
                            formatter: (_: TValue, args: IFormatOptions) => {
                                const employee = args.storage.getValue(args.bindingContext.getParent()) as IPrEmployeeEntity;
                                return userNameWithAvatarFormatter(employee.FirstName, { entity: employee }, {
                                    isBold: true,
                                    withLink: false
                                });
                            },
                        },
                    ],
                    customActionButtons: [
                        {
                            id: ADD_EMPLOYEE_ITEM,
                            title: i18next.t("Deduction:Form.AddEmployee"),
                            isDisabled: (args) => {
                                return (
                                    args.storage.isDisabled ||
                                    !!(args.storage as FormStorage).getBackendDisabledFieldMetadata(
                                        args.storage.data.bindingContext.navigate(PrDeductionEntity.Employees)
                                    )
                                );
                            },
                            isVisible: (args) => {
                                const deduction = args.storage.data.entity as IPrDeductionEntity;
                                return (
                                    deduction.TypeCode === PrDeductionTypeCode.Custom &&
                                    deduction.CurrentTemporalPropertyBag?.IsIncludingAllEmployees === false
                                );
                            },
                            isTransparent: false,
                            position: ActionButtonPosition.Before,
                        },
                    ],
                },
            },
            {
                id: "documents",
                tabs: [
                    {
                        id: "deponation",
                        title: i18next.t("Deduction:Form.TabDeponation"),
                        rows: [[{ id: DEPOSITED_DEDUCTIONS_FIELD }]],
                    },
                    {
                        id: "liabilities",
                        title: i18next.t("Deduction:Form.TabLiabilities"),
                        rows: [[{ id: PrDeductionEntity.PayslipDocuments }]],
                    },
                    {
                        id: "files",
                        title: i18next.t("Deduction:Form.TabFiles"),
                        rows: [[{ id: PrDeductionEntity.DepositClosingDocument }]],
                    },
                    {
                        id: "unpaidChildSupport",
                        title: i18next.t("Deduction:Form.TabUnpaidChildSupport"),
                        rows: [[{ id: PrDeductionEntity.UnpaidChildSupportAmounts }]],
                    },
                ],
            },
        ],
    };


    forEachKey(bankAccountFields, (key) => {
        form.fieldDefinition[createPath(PrDeductionEntity.BankAccount, key)] = {
            ...bankAccountFields[key],
            isRequired: true,
            isVisible: isCounterparty,
            customizationData: {
                ...(bankAccountFields[key].customizationData ?? {}),
                useForCustomization: true
            }
        };
    });

    return {
        entitySet: EntitySetName.PrDeductions,
        table,
        form
    };
};

getDefinitions.translationFiles = [
    "Common",
    "NumberRange",
    "Employee",
    getEnumNameSpaceName(EntityTypeName.PrDeductionType),
    getEnumNameSpaceName(EntityTypeName.PrDistrainmentDeductionType),
    getEnumNameSpaceName(EntityTypeName.PrChildSupportDeductionType),
    getEnumNameSpaceName(EntityTypeName.PrDeductionCalculationType),
    getEnumNameSpaceName(EntityTypeName.PrEmployeeState),
    "Deduction"
];

setDefByEntityType(EntityTypeName.PrDeduction, getDefinitions);
